import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  Stack,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Webcam from "react-webcam";
import Keypad from "./Keypad";
import { SALENG_SHOP_API_PROD } from "../config";
import { useTheme } from "@mui/material/styles";

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

export default function WeightDialog({
  open,
  onClose,
  onConfirm,
  transaction,
}) {
  const theme = useTheme();

  const [heavy, setHeavy] = useState("");
  const [light, setLight] = useState("");
  const [focused, setFocused] = useState("heavy"); // "heavy" | "light"
  const [inputValue, setInputValue] = useState("0");
  const [selectedId, setSelectedId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const webcamRef = useRef(null);

  // ---------- helpers ----------
  const display2 = (val) => Number(val || 0).toFixed(2);

  const sanitizeNumber = (s) => {
    if (s == null || s === "") return "0";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x === "" ? "0" : x;
  };

  const netNumber = useMemo(() => {
    const h = parseFloat(heavy || "0");
    const l = parseFloat(light || "0");
    const n = h - l;
    return Number.isNaN(n) ? 0 : n;
  }, [heavy, light]);

  const isNetNegative = netNumber < 0;

  // ---------- sync จาก transaction ----------
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devs) => {
      const videoInputs = devs.filter((d) => d.kind === "videoinput");
      setDevices(videoInputs);
      if (videoInputs.length > 0) {
        setSelectedDeviceId(videoInputs[0].deviceId); // default
      }
    });
    setHeavy(
      transaction?.carHeavyWeight != null
        ? String(transaction.carHeavyWeight)
        : "0"
    );
    setLight(
      transaction?.carLightWeight != null
        ? String(transaction.carLightWeight)
        : "0"
    );
  }, [transaction]);

  // ---------- sync keypad ตามช่องที่โฟกัส ----------
  useEffect(() => {
    setInputValue(focused === "light" ? light || "0" : heavy || "0");
  }, [focused, heavy, light]);

  // ---------- กล้อง & อัปโหลด ----------
  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return null;
    const video = webcamRef.current.video;
    if (!video || video.readyState < 2) return null;

    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    if (!imageSrc) return null;

    const blob = await (await fetch(imageSrc)).blob();
    return new File([blob], "webcam_photo.jpg", { type: "image/jpeg" });
  }, []);

  async function uploadFile(file) {
    if (!file) throw new Error("No file to upload");
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(SALENG_SHOP_API + "/files", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) throw new Error("Upload failed: " + response.statusText);
    const result = await response.json();
    return result;
  }

  const handleConfirm = async () => {
    let imageUrl = null;
    try {
      const capturedImage = await captureImage();
      if (capturedImage) {
        const uploadResult = await uploadFile(capturedImage);
        imageUrl = uploadResult?.file ?? null;
      }
    } catch (err) {
      console.log("ถ่าย/อัปโหลดรูปไม่สำเร็จ จะดำเนินการต่อโดยไม่แนบรูป:", err);
    }

    onConfirm?.({
      heavy: Number(sanitizeNumber(heavy)),
      light: Number(sanitizeNumber(light)),
      net: netNumber,
      imageUrl,
    });
  };

  // ปุ่มยืนยัน: อนุญาตเมื่อมีค่าซักช่อง และสุทธิไม่ติดลบ
  const canConfirm = (Number(heavy) > 0 || Number(light) > 0) && !isNetNegative;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        น้ำหนักรถขนส่ง
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Grid container spacing={4} mb={5}>
          {/* กล้อง */}
          <Grid item xs={12} md={12} lg={4} sx={{ display: "flex" }}>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  flex: 1,
                  display: "flex",
                  backgroundColor: "black",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative", // 👈 ต้องมี
                }}
              >
                <Webcam
                  ref={webcamRef}
                  key={selectedDeviceId}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={1}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  videoConstraints={{
                    width: 1920,
                    height: 1080,
                    deviceId: selectedDeviceId
                      ? { exact: selectedDeviceId }
                      : undefined,
                  }}
                />

                <Select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(255,255,255,0.9)", 
                    borderRadius: 1,
                    minWidth: 100,
                  }}
                >
                  {devices.map((d) => (
                    <MenuItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `กล้อง ${d.deviceId}`}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>

          {/* ฟอร์ม */}
          <Grid item xs={12} md={12} lg={5}>
            <Stack spacing={2} sx={{ pr: { md: 2 } }}>
              {/* รถหนัก */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5 }}
                  color="text.secondary"
                >
                  รถหนัก
                </Typography>
                <TextField
                  fullWidth
                  value={heavy}
                  onFocus={() => setFocused("heavy")}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontWeight: 700,
                      fontSize: 24,
                      bgcolor:
                        focused === "heavy"
                          ? "action.hover"
                          : "background.paper",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">กก.</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* รถเบา */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5 }}
                  color="text.secondary"
                >
                  รถเบา
                </Typography>
                <TextField
                  fullWidth
                  value={light}
                  onFocus={() => setFocused("light")}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontWeight: 700,
                      fontSize: 24,
                      bgcolor:
                        focused === "light"
                          ? "action.hover"
                          : "background.paper",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">กก.</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* สุทธิ */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5 }}
                  color="text.secondary"
                >
                  สุทธิ
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 24,
                    color: isNetNegative ? "error.main" : "text.primary",
                  }}
                >
                  {display2(netNumber)}{" "}
                  <Typography component="span" color="text.secondary">
                    กก.
                  </Typography>
                </Typography>
                {isNetNegative && (
                  <Typography variant="caption" color="error.main">
                    สุทธิห้ามติดลบ
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>

          {/* Keypad */}
          <Grid
            item
            xs={12}
            md={12}
            lg={3}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Paper elevation={0} sx={{ p: 1 }}>
              <Keypad
                value={inputValue}
                onChange={(val) => setInputValue(val)}
                onClear={() => {
                  setInputValue("0");
                  if (focused === "heavy") setHeavy("0");
                  if (focused === "light") setLight("0");
                }}
                onSubmit={(result) => {
                  const val = sanitizeNumber(String(result ?? ""));
                  if (focused === "light") {
                    setLight(val);
                    setFocused("heavy");
                  } else {
                    setHeavy(val);
                    setFocused("light");
                  }
                  setInputValue("0");
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          // disabled={!canConfirm}
          sx={{
            borderRadius: 0,
            py: 1.8,
            fontWeight: 700,
            bgcolor: "#1E2630",
            "&:hover": { bgcolor: "#111820" },
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
